/**
 * How this organisation writes a calendar date.
 *
 * `08/03/2026` is the third of August to a Romanian and the eighth of March to
 * an American, and on a due list that difference is a month's worth of margin.
 * So which convention the screens use is an organisation setting, on
 * *Organisation info → Organisation details*, beside the hours convention.
 *
 * This is the browser's half. The server's half is
 * `apps/api/src/domain/date-format.ts`, and the two must produce the same
 * strings — the digest and the helper write dates the way the screens do.
 * Change one, change both; `date-format.spec.ts` pins them.
 *
 * Date boxes follow it too — see `date-input.ts`, which puts a box in the
 * organisation's convention in front of every native one.
 *
 * ## What it does not reach
 *
 * **PDFs.** A certificate of release, an estimate and an invoice write dates as
 * `2026-08-21`, the ISO form, on purpose: unambiguous in every country the
 * document may be read in.
 */

export type DateFormat = 'D_MMM_YYYY' | 'DD_MM_YYYY' | 'MM_DD_YYYY';

export const DATE_FORMATS: readonly DateFormat[] = ['D_MMM_YYYY', 'DD_MM_YYYY', 'MM_DD_YYYY'];

export const DATE_FORMAT_LABEL: Record<DateFormat, string> = {
  D_MMM_YYYY: 'Day, month, year — 21 Aug 2026',
  DD_MM_YYYY: 'dd/mm/yyyy — 21/08/2026',
  MM_DD_YYYY: 'mm/dd/yyyy — 08/21/2026',
};

const isDateFormat = (v: unknown): v is DateFormat =>
  typeof v === 'string' && (DATE_FORMATS as readonly string[]).includes(v);

/**
 * The convention in force. Module state for the same reason as the hours
 * convention: about a hundred and fifty call sites read it, and it is set once
 * from the session and again when somebody saves the setting.
 */
let current: DateFormat = 'D_MMM_YYYY';

export const setDateFormat = (v: unknown): void => {
  current = isDateFormat(v) ? v : 'D_MMM_YYYY';
};

export const dateFormat = (): DateFormat => current;

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const two = (n: number): string => String(n).padStart(2, '0');

/**
 * A date in a given convention. Read in UTC: every date in the records is a
 * calendar day stored at midnight UTC, and `21 Aug 2026` has to be the 21st
 * wherever it is read.
 */
export function formatDate(d: Date, format: DateFormat): string {
  const day = d.getUTCDate();
  const month = d.getUTCMonth();
  const year = d.getUTCFullYear();
  switch (format) {
    case 'DD_MM_YYYY':
      return `${two(day)}/${two(month + 1)}/${year}`;
    case 'MM_DD_YYYY':
      return `${two(month + 1)}/${two(day)}/${year}`;
    default:
      return `${day} ${MONTHS[month]} ${year}`;
  }
}

/** A date in the organisation's convention, or a dash for nothing. */
export const fmtDate = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  return formatDate(new Date(iso), current);
};
